/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo } from 'react';
import { useGetUserEducationAssignments } from '../../services/query/user/user';
import { Patient } from '../../types';
import { BookOpen, Video, FileText, Eye, Calendar, Loader2, AlertCircle } from 'lucide-react';
import Pagination from '../shared/Pagination';
import type { PaginationType } from '../../types';

interface Props {
  patient: Patient;
}

export default function EducationalHistory({ patient }: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: educationData, isLoading, isError } = useGetUserEducationAssignments({
    id: Number(patient.id),
    limit: itemsPerPage,
    page: currentPage
  });

  const educationAssignments = educationData?.data ?? [];

  // Transform API pagination response to PaginationType
  const paginationData: PaginationType = useMemo(() => ({
    page: educationData?.pagination?.page ?? currentPage,
    limit: educationData?.pagination?.limit ?? itemsPerPage,
    total_pages: educationData?.pagination?.total_pages ?? 0,
    total_items: educationData?.pagination?.total_items ?? 0,
    total_appointments: educationData?.pagination?.total_items ?? 0,
    appointments_per_page: educationData?.pagination?.limit ?? itemsPerPage,
    has_next_page: educationData?.pagination?.has_next_page ?? false,
    has_previous_page: educationData?.pagination?.has_previous_page ?? false,
  }), [educationData, currentPage, itemsPerPage]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-green-600 mr-2" />
        <span className="text-gray-600">Loading educational content...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12 text-red-500">
        <AlertCircle className="w-8 h-8 mr-2" />
        <span>Failed to load educational content. Please try again.</span>
      </div>
    );
  }

  if (educationAssignments.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Educational Content Delivered</h3>
        <p className="text-gray-500">Educational materials will appear here once assigned to this patient</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Educational Content History</h3>
        <div className="text-sm text-gray-600">
          {paginationData.total_items} item{paginationData.total_items !== 1 ? 's' : ''} assigned
        </div>
      </div>

      <div className="space-y-4">
        {educationAssignments.map((assignment) => (
          <EducationItem key={assignment.id} assignment={assignment} />
        ))}
      </div>

      {/* Pagination */}
      {educationAssignments.length > 0 && (
        <Pagination
          pagination={paginationData}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          itemName="assignments"
        />
      )}
    </div>
  );
}

function EducationItem({ assignment }: { assignment: any }) {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
      case 'video_script':
        return <Video className="w-5 h-5" />;
      case 'pdf':
        return <FileText className="w-5 h-5" />;
      case 'article':
        return <BookOpen className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'video':
      case 'video_script':
        return 'bg-red-100 text-red-800';
      case 'pdf':
        return 'bg-blue-100 text-blue-800';
      case 'article':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-6">
        {/* Content Info */}
        <div className="flex items-start space-x-4 mb-4">
          {assignment.video_thumbnail_url ? (
            <img
              src={assignment.video_thumbnail_url}
              alt={assignment.content_title}
              className="w-24 h-24 object-cover rounded-lg"
            />
          ) : (
            <div className={`w-24 h-24 rounded-lg ${getTypeColor(assignment.content_type)} flex items-center justify-center`}>
              {getTypeIcon(assignment.content_type)}
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">{assignment.content_title}</h4>
                {assignment.content_description && (
                  <p className="text-sm text-gray-600 mb-2">{assignment.content_description}</p>
                )}
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(assignment.content_type)}`}>
                {assignment.content_type}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 text-xs text-gray-600">
              <span className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>Diagnosis: {assignment.diagnosis}</span>
              </span>
              {assignment.video_channel && (
                <span className="flex items-center space-x-1">
                  <span>{assignment.video_channel}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Assignment Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <label className="text-xs text-gray-600 font-medium">Scheduled Date</label>
              <p className="text-gray-900">{formatDate(assignment.scheduled_send_date)}</p>
            </div>
            <div>
              <label className="text-xs text-gray-600 font-medium">Scheduled Time</label>
              <p className="text-gray-900">{assignment.scheduled_send_time}</p>
            </div>
            <div>
              <label className="text-xs text-gray-600 font-medium">Sent At</label>
              <p className="text-gray-900">{formatDate(assignment.sent_at)}</p>
            </div>
            <div>
              <label className="text-xs text-gray-600 font-medium">Status</label>
              <span className={`inline-block px-2 py-1 rounded text-xs font-semibold capitalize ${getStatusColor(assignment.status)}`}>
                {assignment.status}
              </span>
            </div>
          </div>

          {assignment.failure_reason && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <label className="text-xs text-gray-600 font-medium">Failure Reason</label>
              <p className="text-sm text-red-600 mt-1">{assignment.failure_reason}</p>
            </div>
          )}

          {assignment.treatment_plan && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <label className="text-xs text-gray-600 font-medium">Treatment Plan</label>
              <p className="text-sm text-gray-700 mt-1">{assignment.treatment_plan}</p>
            </div>
          )}
        </div>

        {/* Return Date */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-blue-900">Return Date</span>
            </div>
            <span className="text-sm font-medium text-blue-900">{formatDate(assignment.return_date)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
